/**
 * A player's face: their photo, a drawing, or the emoji they fall back to.
 *
 * One stored size (256×256) serves every use here, from 24 px on the table to
 * 40 px in the leaderboard. Storing several sizes would mean several files to
 * sweep and several caches to invalidate for one picture nobody will ever see
 * larger than a thumbnail.
 *
 * The seat colour is a **ring**, never a filled disc. `SEAT_COLORS` was chosen
 * and checked against a dark background; using those same colours as a
 * background is outside what that check covers.
 */

import { useState } from 'react';

export interface AvatarSubject {
  name: string;
  /** Null and undefined both mean "fall back to the default face". */
  emoji?: string | null;
  /** File name only. The URL is built here so it cannot be built wrong twice. */
  avatar?: string | null;
}

export default function Avatar({
  player,
  size = 32,
  seatColor,
  className = '',
}: {
  player: AvatarSubject;
  size?: number;
  /** When given, drawn as a ring around the picture. */
  seatColor?: string;
  className?: string;
}) {
  const [failed, setFailed] = useState(false);

  const style = {
    width: size,
    height: size,
    ...(seatColor ? { boxShadow: `0 0 0 2px ${seatColor}` } : {}),
  };

  const shell = `inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-base-700 ${className}`;

  if (player.avatar && !failed) {
    return (
      <img
        src={`/avatars/${player.avatar}`}
        alt={player.name}
        width={size}
        height={size}
        style={style}
        className={`${shell} object-cover`}
        /*
         * A picture that will not load falls back to the emoji rather than
         * leaving a broken-image icon on the table. It happens for a genuine
         * reason: the phone is offline and this face was never cached.
         */
        onError={() => setFailed(true)}
        loading="lazy"
        decoding="async"
      />
    );
  }

  return (
    <span
      style={style}
      className={shell}
      role="img"
      aria-label={player.name}
      // Scaled to the circle so one component works from 24 px to 40 px.
      title={player.name}
    >
      <span style={{ fontSize: Math.round(size * 0.58), lineHeight: 1 }} aria-hidden>
        {player.emoji || '🙂'}
      </span>
    </span>
  );
}
